import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DrawerLink } from "../../app/DrawerLink";
import { Explicit } from "../../components/ui/Explicit";
import { FavoriteButton } from "../../components/ui/FavoriteButton";
import { api } from "../../lib/api";
import { rooms } from "./api";

/**
 * What this room has already played.
 *
 * ## Why a host needs it
 *
 * Once a track leaves the deck it is gone from every surface they have.
 * Somebody in the chat asks what was playing two songs ago and there is nothing
 * to look at - and the review box lives on the deck, so the chance to say
 * anything about a track ended when the track did.
 *
 * So this is not an archive. It is the answer to "what was that?" and a second
 * chance to act on it: the stats pages are where a whole history belongs.
 *
 * ## Skips are shown, marked
 *
 * The public overlay hides them, because viewers have no business seeing a
 * host's editorial decisions. This is the host's own record of what they did,
 * and "which one did I drop out of?" has no other answer.
 */

export interface Play {
  id: number;
  /** Null for a track that is not in this platform's catalogue. */
  track_id: number | null;
  title: string;
  artist: string;
  artist_username: string | null;
  artwork: string | null;
  explicit: boolean;
  favorited: boolean;
  like_count: number;
  /** Already waiting to play again, so it cannot be added twice. */
  queued: boolean;
  submitter: { username: string; display_name: string } | null;
  boost_tier: "free" | "basic" | "priority" | "earned" | "pick";
  started_at: string;
  skipped: boolean;
}

export function HistoryTable({
  live,
  onQueued,
}: {
  live: boolean;
  /** So the queue tab and the session counts refresh with this list. */
  onQueued: () => void;
}) {
  const client = useQueryClient();

  const history = useQuery({
    queryKey: ["station-history"],
    queryFn: () => api.get<{ history: Play[] }>("/me/station/history"),
    /*
     * Refreshed while a set is running, still while it is not.
     *
     * A track lands here every few minutes at most, so this is generous - and
     * off air nothing will ever change it, which makes any polling at all a
     * request per interval for a list nobody is watching change.
     */
    refetchInterval: live ? 30_000 : false,
  });

  const plays = history.data?.history ?? [];

  /*
   * Re-queued as a *host* addition, not as the original submitter.
   *
   * The person who first sent this track may be nowhere near the room now, and
   * the queue's weighting gives real bonuses for "submitter in the room" and
   * "first time in this room". Re-crediting them would put those bonuses on
   * somebody who is not there and quietly reorder the queue around a fiction.
   *
   * The history row still shows who originally sent it, which is the part worth
   * remembering.
   */
  const requeue = useMutation({
    mutationFn: (trackId: number) => rooms.add(trackId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["station-history"] });
      onQueued();
    },
  });

  if (history.isPending) {
    return <p className="font-mono text-[11px] text-ghost">loading…</p>;
  }

  if (plays.length === 0) {
    return (
      <p className="font-mono text-[11px] leading-relaxed text-ghost">
        nothing played yet — tracks appear here as the set goes on
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5" data-history>
      {plays.map((play) => (
        <li
          key={play.id}
          data-history-play
          data-skipped={play.skipped || undefined}
          className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line px-3 py-2.5"
        >
          {play.artwork ? (
            <img
              src={play.artwork}
              alt=""
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)] object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, #151530 0 6px, #101024 6px 12px)",
              }}
            />
          )}

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              {play.explicit && <Explicit />}

              {/*
                Linked only when there is somewhere to go. A station can carry a
                track that is not ours, and a link to nothing is worse than
                plain text that never promised one.
              */}
              {play.track_id ? (
                <DrawerLink
                  to={`/tracks/${play.track_id}`}
                  className="truncate text-sm font-medium text-ink hover:underline"
                >
                  {play.title}
                </DrawerLink>
              ) : (
                <span className="truncate text-sm font-medium text-ink">
                  {play.title}
                </span>
              )}

              {play.skipped && (
                <span
                  className="meta-caps shrink-0 text-ghost"
                  data-history-skipped
                  title="You skipped this one"
                >
                  skipped
                </span>
              )}
            </div>

            <div className="meta truncate text-faint">
              {play.artist}
              {play.submitter && (
                <>
                  {" · "}
                  {/*
                    Who put it there, which is half the point of looking.
                    Thanking the person who submitted the track everybody just
                    reacted to is a thing hosts do, and they cannot do it if the
                    name went with the track.
                  */}
                  <span data-history-submitter>@{play.submitter.username}</span>
                </>
              )}
              {play.boost_tier !== "free" && (
                <>
                  {" · "}
                  <span className="text-soft">{play.boost_tier}</span>
                </>
              )}
              {" · "}
              {clock(play.started_at)}
            </div>
          </div>

          {/*
            Acting on it after the fact, which is the whole reason the row has
            controls at all. Reviewing is reachable from the track page - the
            host has played it, so the review gate lets them in.

            Nothing here for a track that is not in the catalogue: there is no
            id to favourite or queue, and a disabled control for something that
            could never work is worse than no control.
          */}
          {play.track_id && (
            <>
              {/*
                Disabled rather than hidden when it is already waiting.

                A station cannot have the same track queued twice - a unique
                index says so - and a button that fails on click teaches people
                not to trust the row. The title says which case it is.
              */}
              <button
                type="button"
                onClick={() => requeue.mutate(play.track_id as number)}
                /*
                 * Only this row waits, not all of them.
                 *
                 * `isPending` on the shared mutation would disable every
                 * button on the list while one add was in flight, which reads
                 * as the whole panel seizing up over a single click.
                 */
                disabled={play.queued || requeue.variables === play.track_id}
                data-history-requeue={play.track_id}
                aria-label={
                  play.queued ? "Already in the queue" : `Queue ${play.title} again`
                }
                title={
                  play.queued
                    ? "already in the queue"
                    : "add this to the queue again"
                }
                className="meta-caps shrink-0 rounded-[var(--radius-pill)] border border-line px-2.5 py-1 text-soft transition-colors hover:border-line-lit hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-line disabled:hover:text-soft"
              >
                {play.queued ? "queued" : "+ queue"}
              </button>

              <FavoriteButton
                trackId={play.track_id}
                favorited={play.favorited}
                likeCount={play.like_count}
                size="sm"
                className="shrink-0"
              />
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * `14:32`, in the host's own timezone.
 *
 * Not "8 minutes ago": a host scanning for the track somebody is asking about
 * is matching it against when they remember hearing it, and a clock time is
 * what a chat message sits next to.
 */
function clock(iso: string): string {
  const at = new Date(iso.replace(" ", "T"));

  if (Number.isNaN(at.getTime())) return "";

  return at.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
